import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { type ServerError, useNotifier } from "./useNotifier";

export type GraphqlQueryResult<TResult> = {
	data: TResult | null;
	errors: ServerError[] | null;
	exception: unknown;
};

// Helper functions to create GraphqlQueryResult objects
export function ResultFromData<TResult>(data: TResult): GraphqlQueryResult<TResult> {
	return { data, errors: null, exception: null };
}

export function ResultFromError<TResult>(errors: ServerError[]): GraphqlQueryResult<TResult> {
	return { data: null, errors, exception: null };
}

export function ResultFromException<TResult>(exception: unknown): GraphqlQueryResult<TResult> {
	return { data: null, errors: null, exception };
}

export type NotificationGraphqlOptions<TArgs, TResult> = {
	graphqlCall: (args: TArgs) => Promise<GraphqlQueryResult<TResult>>;
	onComplete: (item: TResult) => void;
	successMessage?: string;
};

export type NotificationGraphqlHook<TArgs> = {
	save: (args: TArgs) => Promise<void>;
	loading: boolean;
};

export function useNotificationGraphql<TArgs, TResult>(
	options: NotificationGraphqlOptions<TArgs, TResult>,
): NotificationGraphqlHook<TArgs> {
	const { t } = useTranslation();
	const [loading, setLoading] = useState(false);
	const { notifySuccess, notifyFailure, notifyErrors, notifyException } = useNotifier();

	const save = useCallback(
		async (args: TArgs) => {
			setLoading(true);

			try {
				// call
				const result = await options.graphqlCall(args);

				if (result.errors && result.errors.length > 0) {
					notifyErrors(result.errors);
					return;
				}

				if (result.exception != null) {
					notifyException(result.exception);
					return;
				}

				if (result.data == null) {
					notifyFailure(t("no-data-available"));
					return;
				}

				if (options.successMessage != null) {
					notifySuccess(options.successMessage);
				}

				options.onComplete(result.data);
			} catch (error) {
				notifyException(error);
			} finally {
				setLoading(false);
			}
		},
		[options, t, notifyErrors, notifyException, notifyFailure, notifySuccess],
	);

	return { save, loading };
}
